<template>
	<panel class="completion-panel-section" :show="show" @close="close">
		<template #title>
			Add Note
		</template>
		<template>
			<form
				class="panel-form panel-form-textarea-fullheight"
				@submit.prevent="onFormSubmit"
			>
				<img
					src="@/assets/img/undraw-noted.svg"
					class="panel-illustration"
				/>
				<span class="mb-4 font-roboto text-center">
					Enter Completion Note
					{{ completionNoteRequired ? '(Required)' : '' }}
				</span>
				<textarea
					ref="completionTextarea"
					v-model="message"
					placeholder="Enter new note here..."
					class="resize-none px-1 py-2 border rounded focus:outline-none focus:shadow-outline font-roboto mb-2 full-length-textarea"
				></textarea>
				<div
					v-if="textAreaErrorMessage"
					class="text-red-500 text-base mb-2"
				>
					{{ textAreaErrorMessage }}
				</div>
				<div class="flex justify-between mt-3">
					<button
						type="button"
						class="btn btn-danger w-50 mr-2"
						@click.prevent="close"
					>
						<font-awesome-icon
							class="btn-icon"
							:icon="['far', 'times-octagon']"
						/>
						<span class="btn-label">Cancel</span>
					</button>
					<button
						ref="last"
						type="submit"
						:disabled="isPageCompleting"
						:class="[
							'btn',
							'w-50',
							'ml-2',
							isValidMessage ? 'btn-outline-success' : '',
						]"
						:aria-disabled="!isValidMessage"
						@keydown.tab="onLastElementTab"
					>
						<div class="btn-ripple" />
						<font-awesome-icon
							v-if="isPageCompleting"
							class="btn-icon fa-spin mr-2"
							:icon="['far', 'circle-notch']"
						/>
						<font-awesome-icon
							v-else
							class="btn-icon"
							:icon="['far', 'check']"
							aria-hidden="true"
						/>
						<span class="btn-label">Complete</span>
					</button>
				</div>
			</form>
		</template>
	</panel>
</template>
<script>
import Panel from './Panel'
import { mapActions, mapGetters } from 'vuex'
import PanelMixin from '@/mixins/PanelMixin'
import util from '@/util'

export default {
	/**
	 * The component's registered child components.
	 *
	 * @type {Object}
	 */
	components: {
		Panel,
	},

	computed: {
		currentPage() {
			return this.findBySid(this.sid)
		},

		completionTemplate() {
			return (this.activePartner?.complete_note_template || '')
				.trim()
				.replace(/([:-])\s+(?=\r?\n)/g, '$1')
		},

		completionNoteRequired() {
			return Boolean(this.activePartner?.completion_note_required)
		},

		isValidMessage() {
			return util.containNewAlphabets(
				this.message,
				this.completionTemplate,
				this.completionNoteRequired ? 2 : 0,
				500
			)
		},

		...mapGetters({
			activePartner: 'partners/active',
			findBySid: 'pages/findBySid',
		}),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		/**
		 * Handle the form submit event.
		 *
		 * @return {void}
		 */
		async onFormSubmit() {
			if (!this.isValidMessage) {
				if (this.completionNoteRequired) {
					this.textAreaErrorMessage =
						'A completion note of 2-500 characters is required'
				} else {
					this.textAreaErrorMessage =
						'A completion note should not exceed 500 characters.'
				}
				return
			}
			try {
				this.isPageCompleting = true
				await this.complete({
					sid: this.sid,
					message:
						util.normalizeString(this.completionTemplate) ===
						util.normalizeString(this.message)
							? ''
							: this.message.trim(),
					closeDrawer: this.close,
				})

				this.message = ''

				this.$alert.success('pages.complete.success')
			} catch (e) {
				this.$alert.error('pages.complete.error')
			} finally {
				this.isPageCompleting = false
				this.close()
			}
		},

		handleSetMessage() {
			const storedMessage = util.getDraftValue('complete_page', this.sid)
			if (!storedMessage && this.completionTemplate) {
				return (this.message = this.completionTemplate)
			}
			return (this.message = storedMessage || '')
		},

		focusCompletionTextarea() {
			this.$nextTick(() => {
				setTimeout(() => {
					const textarea = this.$refs.completionTextarea
					if (textarea) {
						textarea.focus()
						textarea.setSelectionRange(0, 0)
					}
				}, 200)
			})
		},

		...mapActions({
			complete: 'pages/complete',
		}),
	},

	/**
	 * The component's mergeable mixins.
	 *
	 * @type {Array}
	 */
	mixins: [PanelMixin],

	/**
	 * The component's name used for debugging.
	 *
	 * @type {String}
	 */
	name: 'CompletePagePanel',

	/**
	 * The component's inherited properties.
	 *
	 * @type {Object}
	 */
	props: {
		/**
		 * The page's sid to be dismissed.
		 *
		 * @type {String}
		 */
		sid: {
			required: true,
			validator: prop => typeof prop === 'string' || prop === null,
		},

		show: {
			type: Boolean,
		},
	},

	watch: {
		show(isVisible) {
			this.textAreaErrorMessage = ''
			if (isVisible) {
				if (this.completionNoteRequired) {
					this.focusCompletionTextarea()
				}
				this.handleSetMessage()
			}
		},
		message() {
			this.textAreaErrorMessage = ''
			util.setDraftValue('complete_page', this.message, this.sid)
		},
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			message: '',
			isPageCompleting: false,
			textAreaErrorMessage: '',
		}
	},
}
</script>
